import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatMoney } from '@/utils/format';

import type { AssignedCoupon } from './subscriptions-types';

const discountLabel = (
  coupon: AssignedCoupon,
  t: (key: string, options?: Record<string, string | number>) => string,
): string => {
  if (coupon.discountType === 'percent') {
    return t('assignedCoupon.percent', { value: coupon.discountValue });
  }
  if (coupon.discountType === 'free_months') {
    return t('assignedCoupon.months', { count: coupon.discountValue });
  }
  return t('assignedCoupon.amount', { amount: formatMoney(coupon.discountValue, 'GHS') });
};

const durationLabel = (
  coupon: AssignedCoupon,
  t: (key: string, options?: Record<string, string | number>) => string,
): string => {
  if (coupon.discountType === 'free_months' || coupon.durationType === 'once') {
    return t('assignedCoupon.durationOnce');
  }
  if (coupon.durationType === 'forever') return t('assignedCoupon.durationForever');
  return t('assignedCoupon.durationMonths', { count: coupon.durationMonths ?? 0 });
};

export const AssignedCouponsCard = ({
  coupons,
  canRedeem,
  onRedeem,
}: {
  coupons: AssignedCoupon[];
  canRedeem: boolean;
  onRedeem: (code: string) => void;
}) => {
  const { t } = useTranslation('billing');
  if (coupons.length === 0) return null;

  return (
    <div className="space-y-4">
      {coupons.map((coupon) => {
        const available = coupon.status === 'available';
        return (
          <Card key={coupon.id} className="border-brand/30">
            <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1">
                <CardTitle>
                  {available ? t('assignedCoupon.title') : t('assignedCoupon.appliedTitle')}
                </CardTitle>
                <p className="text-heading-sm font-semibold text-fg">
                  {discountLabel(coupon, t)}
                </p>
                <p className="font-mono text-caption text-fg-secondary">{coupon.code}</p>
                <CardDescription>{durationLabel(coupon, t)}</CardDescription>
                {!available ? (
                  <p className="text-body-sm text-fg-secondary">{t('assignedCoupon.applied')}</p>
                ) : null}
                {available && !canRedeem ? (
                  <p className="text-body-sm text-fg-secondary">
                    {t('assignedCoupon.redeemWhenReady')}
                  </p>
                ) : null}
              </div>
              {available && canRedeem ? (
                <Button type="button" className="shrink-0" onClick={() => onRedeem(coupon.code)}>
                  {t('assignedCoupon.redeem')}
                </Button>
              ) : null}
            </CardHeader>
          </Card>
        );
      })}
    </div>
  );
};
